import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col, Panel, Toast } from 'emerald-ui';
import SignUpFormContactInfo from '../SignUpFormContactInfo';
import SignUpFormPrimaryAddress from '../SignUpFormPrimaryAddress';
import SignUpFormRegistrationInfo from '../SignUpFormRegistrationInfo';
import SignUpFormMailingAddress from '../SignUpFormMailingAddress';
import SignUpFormCompanyInfo from '../SignUpFormCompanyInfo';
import SignUpFormUserInfo from '../SignUpFormUserInfo';
import SignUpFormAccessInfo from '../SignUpFormAccessInfo';
import SignUpFormFooter from '../SignUpFormFooter';
import * as signUpRequest from '../../api/signUpForm';
import * as authRequest from '../../api/auth';
import { PROVIDER_SUITE_URL, LAUNCHPAD_URL } from '../../config';
import { validateProviderInfo, validateUserInfo, hasFormErrors } from './schema';
import { removeAlfa, timeout } from '../../utils';
import './SignUpForm.css';

class SignUpForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      provider: {
        name: '',
        phone: '',
        phoneExt: '',
        tollFreePhone: '',
        tollFreePhoneExt: '',
        fax: '',
        streetAddress: '',
        houseNumber: '',
        country: 'US',
        city: '',
        state: '',
        zipCode: '',
        isAddressOutsideUS: false,
        isMailAddressSameAsPhysical: true,
        mailStreetAddress: '',
        mailHouseNumber: '',
        mailCountry: 'US',
        mailCity: '',
        mailState: '',
        mailZipCode: '',
        mailIsAddressOutsideUS: false,
        registrationPhone: '',
        courseRegistrationWebsite: '',
        companyWebsite: '',
        isHouse: true,
      },
      user: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        username: '',
        password: '',
        confirmPassword: '',
      },
      toast: {
        isVisible: false,
        message: '',
      },
      providerSchema: {},
      userSchema: {},
    };
  }

  componentWillMount() {
    const providerSchema = validateProviderInfo(this.state.provider);
    const userSchema = validateUserInfo(this.state.user);
    this.setState({ providerSchema, userSchema });
  }

  handleProviderChange = (newObjValue) => {
    const provider = this.state.provider;
    const newProvider = { ...provider, ...newObjValue };
    const providerSchema = validateProviderInfo(newProvider);
    this.setState({ provider: newProvider, providerSchema });
  };

  handleUserChange = (newObjValue) => {
    const user = this.state.user;
    const newUser = { ...user, ...newObjValue };
    const userSchema = validateUserInfo(newUser);
    this.setState({ user: newUser, userSchema });
  };

  createProviderAccount = async () => {
    const data = {
      provider: {
        ...this.state.provider,
        phone: removeAlfa(this.state.provider.phone),
        tollFreePhone: removeAlfa(this.state.provider.tollFreePhone),
        fax: removeAlfa(this.state.provider.fax),
        registrationPhone: removeAlfa(this.state.provider.registrationPhone),
      },
      user: {
        ...this.state.user,
        phone: removeAlfa(this.state.user.phone),
      },
      application: {
        board: this.props.board,
        state: this.props.state,
      },
    };
    const signUpResponse = await signUpRequest.signUp(data).catch(() => {
      this.handleOpenCloseToast(true, 'Error creating provider account');
    });
    if (!signUpResponse) {
      return false;
    }
    return signUpResponse;
  };

  getToken = async () => {
    const { username, password } = this.state.user;
    const authResponse = await authRequest.fetchToken(username, password).catch(() => {});
    if (!authResponse) {
      return false;
    }
    return authResponse.data.access_token;
  };

  scrollToError = () => {
    const errorElement = document.querySelector('span.text-field-message');
    if (errorElement) {
      errorElement.scrollIntoView(false);
    }
  };

  showErrors = (section) => {
    const options = { showErrors: true };
    if (section === 'provider') {
      const providerSchema = validateProviderInfo(this.state.provider, options);
      this.setState({ providerSchema }, this.scrollToError);
    }

    if (section === 'user') {
      const userSchema = validateUserInfo(this.state.user, options);
      this.setState({ userSchema }, this.scrollToError);
    }
    this.handleOpenCloseToast(true, 'Verify the errors in the form and try again');
  };

  isFormValid = (section) => {
    const { providerSchema, userSchema, provider } = this.state;

    if (section === 'provider') {
      let fieldsErrorsToIgnore = [];
      if (provider.isMailAddressSameAsPhysical) {
        fieldsErrorsToIgnore = [
          'mailStreetAddress',
          'mailHouseNumber',
          'mailCountry',
          'mailCity',
          'mailState',
          'mailZipCode',
        ];
      }
      if (provider.isAddressOutsideUS) {
        fieldsErrorsToIgnore.push('state');
      }
      if (provider.mailIsAddressOutsideUS) {
        fieldsErrorsToIgnore.push('mailState');
      }
      return !hasFormErrors(providerSchema, fieldsErrorsToIgnore);
    } else if (section === 'user') {
      return !hasFormErrors(userSchema);
    }
  };

  retryWithDelay = async (fn, retries = 3) => {
    const response = await fn();

    if (!response) {
      if (retries <= 0) {
        return false;
      }
      await timeout();
      return this.retryWithDelay(fn, retries - 1);
    }
    return response;
  };

  handleSubmit = async () => {
    const { currentStep } = this.props;
    const section = currentStep === 1 ? 'provider' : 'user';
    if (!this.isFormValid(section)) {
      this.showErrors(section);
      return false;
    }

    const accountResult = await this.createProviderAccount();
    if (!accountResult) {
      return false;
    }
    this.handleOpenCloseToast(true, 'Account created successfully');

    const token = await this.retryWithDelay(this.getToken);

    if (!token) {
      window.location.href = `${LAUNCHPAD_URL}`;
      return false;
    }
    window.location.href = `${PROVIDER_SUITE_URL}/?token=${token}&comesFrom=providerSignup`;
  };

  handleOpenCloseToast = (isVisible = false, message = '') => {
    this.setState({ toast: { isVisible, message } });
  };

  renderStep1 = () => {
    const { provider, providerSchema } = this.state;
    return (
      <section id="step1">
        <Row>
          <Col xs={12} className="sign-up-form-section" style={{ paddingTop: '16px' }}>
            <SignUpFormCompanyInfo handleFieldChange={this.handleProviderChange} name={providerSchema.name} />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="sign-up-form-section">
            <SignUpFormContactInfo
              handleFieldChange={this.handleProviderChange}
              phone={providerSchema.phone}
              phoneExt={providerSchema.phoneExt}
              tollFreePhone={providerSchema.tollFreePhone}
              tollFreePhoneExt={providerSchema.tollFreePhoneExt}
              fax={providerSchema.fax}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="sign-up-form-section">
            <SignUpFormPrimaryAddress
              handleFieldChange={this.handleProviderChange}
              streetAddress={providerSchema.streetAddress}
              houseNumber={providerSchema.houseNumber}
              country={providerSchema.country}
              city={providerSchema.city}
              state={providerSchema.state}
              zipCode={providerSchema.zipCode}
              isAddressOutsideUS={provider.isAddressOutsideUS}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="sign-up-form-section">
            <SignUpFormMailingAddress
              handleFieldChange={this.handleProviderChange}
              isMailAddressSameAsPhysical={provider.isMailAddressSameAsPhysical}
              streetAddress={providerSchema.mailStreetAddress}
              houseNumber={providerSchema.mailHouseNumber}
              country={providerSchema.mailCountry}
              city={providerSchema.mailCity}
              state={providerSchema.mailState}
              zipCode={providerSchema.mailZipCode}
              isAddressOutsideUS={provider.mailIsAddressOutsideUS}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="sign-up-form-section">
            <SignUpFormRegistrationInfo
              handleFieldChange={this.handleProviderChange}
              registrationPhone={providerSchema.registrationPhone}
              courseRegistrationWebsite={providerSchema.courseRegistrationWebsite}
              companyWebsite={providerSchema.companyWebsite}
              isHouse={provider.isHouse}
            />
          </Col>
        </Row>
      </section>
    );
  };

  renderStep2 = () => {
    const { userSchema } = this.state;
    return (
      <section id="step2">
        <Row>
          <Col xs={12} className="sign-up-form-section">
            <SignUpFormUserInfo
              handleFieldChange={this.handleUserChange}
              firstName={userSchema.firstName}
              lastName={userSchema.lastName}
              email={userSchema.email}
              phone={userSchema.phone}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="sign-up-form-section">
            <SignUpFormAccessInfo
              handleFieldChange={this.handleUserChange}
              username={userSchema.username}
              password={userSchema.password}
              confirmPassword={userSchema.confirmPassword}
            />
          </Col>
        </Row>
      </section>
    );
  };

  renderFooter = () => {
    const { currentStep, steps, onStepChange } = this.props;
    return (
      <SignUpFormFooter
        onStepChange={onStepChange}
        steps={steps}
        currentStep={currentStep}
        handleSubmit={this.handleSubmit}
        isFormValid={this.isFormValid}
        showErrors={this.showErrors}
      />
    );
  };

  render() {
    const { currentStep } = this.props;
    const { toast } = this.state;
    return (
      <Grid className="sign-up-form">
        <Panel>
          <Panel.Body>
            {currentStep === 1 && this.renderStep1()}
            {currentStep === 2 && this.renderStep2()}
            {this.renderFooter()}
          </Panel.Body>
        </Panel>
        <Toast message={toast.message} visible={toast.isVisible} onTimeout={this.handleOpenCloseToast} />
      </Grid>
    );
  }
}

SignUpForm.propTypes = {
  currentStep: PropTypes.number,
  onStepChange: PropTypes.func,
  steps: PropTypes.array,
  handleSubmit: PropTypes.func,
  board: PropTypes.number,
  state: PropTypes.number,
};

SignUpForm.defaultProps = {
  currentStep: 1,
  onStepChange: () => {},
  steps: [],
  handleSubmit: () => {},
};

export default SignUpForm;



// WEBPACK FOOTER //
// ./src/components/SignUpForm/SignUpForm.js